import {gql} from '@apollo/client';

import {assert} from 'Asserts';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  DoesInputContainerHaveContainerNameTemplateQuery,
  DoesInputContainerHaveContainerNameTemplateQueryVariables,
} from 'name-templates/__generated/DoesInputContainerHaveContainerNameTemplateQuery';
import {
  GetContainerContentsQuery,
  GetContainerContentsQueryVariables,
} from 'name-templates/__generated/GetContainerContentsQuery';
import {
  GetContainerNameTemplateQuery,
  GetContainerNameTemplateQueryVariables,
} from 'name-templates/__generated/GetContainerNameTemplateQuery';

type ContainerIdWithContentIds = ExtractByTypename<
  DoesInputContainerHaveContainerNameTemplateQuery['containers'][number],
  'ContainerNode'
>;

export const GET_CONTAINER_NAME_TEMPLATE_QUERY = gql`
  query GetContainerNameTemplateQuery($tagSchemaId: StrictString!) @skipAudit @engComponent(name: INVENTORY) {
    tagSchema(id: $tagSchemaId) {
      ... on TagSchemaNode {
        id
        containerNameTemplateParts {
          datetimeFormat
          fieldId
          text
          type
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const DOES_INPUT_CONTAINER_HAVE_CONTAINER_NAME_TEMPLATE_QUERY = gql`
  query DoesInputContainerHaveContainerNameTemplateQuery($containerIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        contents {
          ... on ContainerContentNode {
            id
            containedItemTagSchema {
              ... on TagSchemaNode {
                id
                containerNameTemplateParts {
                  datetimeFormat
                  fieldId
                  text
                  type
                }
              }
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const GET_CONTAINER_CONTENTS_QUERY = gql`
  query GetContainerContentsQuery($containerIds: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        contents {
          ... on ContainerContentNode {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export async function queryTagSchemaHasContainerNameTemplate(tagSchemaId: string): Promise<boolean> {
  /**
   * Returns true if the tag schema has a container name template defined.
   */
  const {tagSchema} = await doQuerySafe<
    GetContainerNameTemplateQuery,
    GetContainerNameTemplateQueryVariables
  >({query: GET_CONTAINER_NAME_TEMPLATE_QUERY}, {variables: {tagSchemaId}});
  assert(!isInaccessible(tagSchema), 'Expected containable tag schema to be accessible');
  return tagSchema.containerNameTemplateParts.length !== 0;
}

export async function queryInputContainerHasNameTemplate(containerId: string): Promise<boolean> {
  /**
   * Returns true if the input container has a name template that could be applied to an output container
   * during a transfer.
   *
   * "Having a name template" here means that the tag schema of the bioentity linked to the first content
   * has a container name template defined.
   */
  const {containers} = await doQuerySafe<
    DoesInputContainerHaveContainerNameTemplateQuery,
    DoesInputContainerHaveContainerNameTemplateQueryVariables
  >(
    {query: DOES_INPUT_CONTAINER_HAVE_CONTAINER_NAME_TEMPLATE_QUERY},
    {variables: {containerIds: [containerId]}}
  );
  const container = containers[0];
  assert(!isInaccessible(container), 'Container must be accessible');
  return (
    container.contents.length > 0 &&
    container.contents[0].containedItemTagSchema.containerNameTemplateParts.length > 0
  );
}

export async function queryForContainerContents(
  containerIds: Array<string>
): Promise<Array<ContainerIdWithContentIds>> {
  /**
   * Returns container content IDs.
   *
   * Used to determine whether output container is empty, which is a necessary condition for a container
   * name template to be applied during a transfer.
   */
  const {containers} = await doQuerySafe<GetContainerContentsQuery, GetContainerContentsQueryVariables>(
    {query: GET_CONTAINER_CONTENTS_QUERY},
    {variables: {containerIds}}
  );
  assert(
    containers.every((container) => !isInaccessible(container)),
    'Containers must be accessible'
  );
  return containers as unknown as Array<ContainerIdWithContentIds>;
}
